// Shared types for the Blast Radius simulation. Everything in src/core is
// deterministic: no Date.now(), no Math.random(), no DOM. The renderer and UI
// read World state; they never mutate it except through World.apply().

export type Dir = 0 | 1 | 2 | 3; // east, south, west, north
export const DX = [1, 0, -1, 0] as const;
export const DY = [0, 1, 0, -1] as const;
export const DIR_NAMES = ['east', 'south', 'west', 'north'] as const;

export const TICK_RATE = 30;
export const DT = 1 / TICK_RATE;

export type ItemKind = 'request' | 'context' | 'spec' | 'draft' | 'build' | 'bug';

export interface Item {
  id: number;
  kind: ItemKind;
  /** A hallucinated draft. Hidden from the player until a bench or the users find it. */
  flawed: boolean;
  /** Incident id when this item carries corruption, else 0. */
  corrupt: number;
  /** Customer record this work changes (0 = none). */
  rec: number;
  /** Version of that record the work was based on when an agent started it. */
  base: number;
}

export type BuildingKind =
  | 'belt'
  | 'router'
  | 'breaker'
  | 'gate'
  | 'indexer'
  | 'specdesk'
  | 'forge'
  | 'bench'
  | 'postmortem'
  | 'users'
  | 'rack'
  | 'monitor'
  | 'firewall'
  | 'checkpoint'
  | 'scanner'
  | 'drones'
  // Map features the player can't place.
  | 'terminal'
  | 'monolith'
  | 'nest'
  | 'cache';

/** Where an action came from. The run report uses this to measure delegation. */
export type Source = 'mouse' | 'keys' | 'command' | 'voice' | 'api' | 'drone' | 'system';

export type Status = 'ok' | 'idle' | 'starved' | 'blocked' | 'nopower' | 'corrupt' | 'tripped' | 'ghost';

export interface BeltSlot {
  item: Item;
  /** Progress along the tile, 0 = entry edge, 1 = exit edge. */
  p: number;
}

export interface Building {
  id: number;
  kind: BuildingKind;
  x: number;
  y: number;
  w: number;
  h: number;
  dir: Dir;
  label: string;
  status: Status;
  /** Crafting progress in seconds. */
  progress: number;
  crafting: boolean;
  /** The item being crafted carries its inputs' traits. */
  craftFlawed: boolean;
  craftFromRequest: boolean;
  /** Record and base version of the work being crafted. */
  craftRec: number;
  craftBase: number;
  inQ: Item[];
  outQ: Item[];
  rejectQ: Item[];
  /** Belt and breaker contents, front item first. */
  slots: BeltSlot[];
  /** Incident id while corrupted, else 0. */
  corrupted: number;
  corruptTimer: number;
  hp: number;
  maxHp: number;
  powered: boolean;
  /** Router round-robin pointer and last input direction. */
  rr: number;
  tripped: Item | null;
  tripTimer: number;
  cooldown: number;
  ghost: boolean;
  placedBy: Source;
  placedAt: number;
  /** Nest charge, scanner sweep, monitor target and similar per-kind state. */
  charge: number;
  targetId: number;
  zapT: number;
  outAlt: number;
}

export interface Glitch {
  id: number;
  x: number;
  y: number;
  hp: number;
  targetId: number;
  attackT: number;
  /** Nest or event that spawned it, for the incident log. */
  origin: string;
  wob: number;
}

export interface Incident {
  id: number;
  t: number;
  cause: 'glitch' | 'flawed-release' | 'dependency-break' | 'legacy-collapse' | 'corrupt-release' | 'lost-update' | 'outage';
  origin: string;
  x: number;
  y: number;
  /** Buildings corrupted by this incident, including the first one. */
  affected: number;
  reachedUsers: boolean;
  contained: boolean;
  resolvedAt: number;
  how: string;
}

export interface Zone {
  id: string;
  name: string;
  owner: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** none = locked, asked = waiting on approval, approved, claimed = taken without asking. */
  state: 'locked' | 'asked' | 'approved' | 'claimed';
}

export interface Approval {
  id: number;
  special: string;
  label: string;
  askedAt: number;
  resolveAt: number;
  willApprove: boolean;
  done: boolean;
  x: number;
  y: number;
}

export interface Secret {
  id: string;
  kind: 'well' | 'vault' | 'terminal' | 'rack' | 'cache';
  name: string;
  x: number;
  y: number;
  found: boolean;
  foundAt: number;
  hint: string;
}

export interface FloatText {
  x: number;
  y: number;
  text: string;
  color: string;
  t: number;
}

export interface WorldEvent {
  t: number;
  type: string;
  src?: Source;
  [k: string]: unknown;
}

export type GameOutcome = 'playing' | 'won' | 'lost';
